/**
 * The **structure-loss interception window** — the control-flow seam that lets an ability try to
 * *un-take* a structure point before the Structure Damage table is ever rolled
 * (`projects/track-damage-event-family.md` Phase 2, Fork A).
 *
 * **This is an interception window, NOT an event family** (`decisions/0112` §1, and
 * `GLOSSARY.md`'s *interception window*). A family fires *after* a pipeline
 * moment and contributes steps; it can never un-take the damage. RAW clauses that say "ignore the
 * damage" or "instead" need this mechanism and can never be served by the family. Custom Paint Job
 * is the worked example: *"When you take structure damage, roll 1d6. On a 6, you return to 1 HP and
 * ignore the damage."*
 *
 * ## Where it runs
 *
 * {@link import("./tables").resolveDamageCascade}, between the `structure_delta` push and the
 * {@link import("./tables").resolveStructureDamage} call. That placement is the whole point: the
 * resolution replays from the top on every round-trip, so a prompt raised *here* is reached before
 * the System Trauma "which system do you destroy" prompt **by construction** — the player is never
 * asked to pick a system to lose to a hit the paint job was about to shrug off.
 *
 * ## Shape
 *
 * A fixed, authored list of {@link StructureInterceptOffer}s, mirroring
 * {@link import("./reactionRegistry")}'s `BRACE_OFFER` / `gatherReaction` pair: eligibility, a
 * stable prompt id, options, and an `outcome` that is pure over `(ctx, answer)`. An eligible
 * offer with no recorded answer **throws {@link NeedsDecisionSignal}** in interactive mode and
 * **defaults to decline** otherwise (replay / CLI / the seven non-interactive cascade callers).
 *
 * ## Eligibility is derived from FROZEN AUTHORED DATA, never a hardcoded id list
 *
 * An offer names the compendium item that authors it (`carrierItemId`) and nothing else. Whether
 * the ability *exists on this combatant at all*, and *how many uses it has*, is read out of
 * `base.affordances` — the table `spawnMech`'s overlay-mint freeze arm builds from the
 * item's own authored `action` clause and its `limitedUses` facet — crossed with the spent count in
 * `current.resources`. That is the same read {@link import("./tables")}'s `isSystemDestroyable`
 * already performs, through the same `canUseSceneAction` door
 * (`economy/actionAvailability.ts`'s HARD RULE: usage reads route through one door).
 *
 * The consequence is the property this design is bought for: **delete the authored clause and the
 * offer goes dark on its own**, because no cap freezes. Re-author `limitedUses.max` and the offer
 * honours the new number. Only the *effect* (the d6, the threshold, the HP restore) lives in code,
 * and `packs/core-rulebook/entries/systems/custom-paint-job.test.ts` pins that half
 * against the shipped clause so the two homes cannot drift silently.
 *
 * Option B — compiling the offer off the authored clause the way `compileAuthoredOffers` does — is
 * the recorded target if a second carrier ever needs this. It is not built today because `overlay`
 * would have to be threaded into `tables.ts` and **zero of the eight `resolveDamageCascade` callers
 * has a `LoadedOverlay` in scope**. See the tracker's "The fork — RULED" for the measurement.
 */

import type { AffordanceUseSetStep, Combatant, InterceptStep, ResolvedStep } from "@lancer-vtt/domain";
import { spendStepsNow } from "./events/gameEvents";
import {
  canUseSceneAction,
  isItemDestroyed,
} from "@lancer-vtt/domain";
import type { DiceSource } from "../dice";
import { clampHp } from "./effects/hp";
// Same transport as Brace and System Trauma — the identical `NeedsDecisionSignal` class
// `resolve/damageApply.ts`'s catch sites already recognize via `instanceof`, never a bespoke prompt type.
import { NeedsDecisionSignal } from "./effects";
import { recordedAnswer, type RecordedAnswers } from "./effects/choices";

/** The recorded-answer option id that FIRES an interception offer. */
export const INTERCEPT_USE_OPTION = "use";
/** The recorded-answer option id that declines one (recorded so replay reproduces the choice). */
export const INTERCEPT_SAVE_OPTION = "save";

/**
 * What the cascade hands an offer at the interception window. Deliberately **neither `state` nor
 * `overlay`** — the frozen cap plus `current.resources` is the whole eligibility read, and both
 * hang off the `target` Combatant the cascade already receives. That is a direct product of the
 * frozen-at-spawn cap (tracker D2); the rejected check-time-derived cap shape would have forced an
 * overlay in here.
 */
export type StructureInterceptContext = {
  /**
   * The combatant taking the structure point, **with the cascade-local resource overlay already
   * merged** (an earlier row's `destroy_item`, an earlier interception's charge spend) — the same
   * overlaid combatant `resolveStructureDamage` is handed, for the same reason.
   */
  target: Combatant;
  /**
   * The cascade's locally-simulated HP at this instant. `0` in an HP-driven cascade (the point that
   * forced the structure loss); the target's own untouched HP in a SUBSTITUTED structure loss
   * (`tables.ts`'s `substituteStructurePoint` — Sniper's Mark), where no HP was ever lost.
   */
  simulatedHp: number;
  /** `current.structure` AFTER the point just marked. The prompt key's discriminator — see {@link structureInterceptPromptId}. */
  remainingStructure: number;
  /** The authority dice. **An offer draws from this ONLY after its answer is known** — see {@link gatherStructureIntercept}. */
  dice: DiceSource;
  /** Recorded prompt answers: an intent's `choices` map, or an effect context's choice source. */
  choices?: RecordedAnswers;
  /** Interactive mode: an eligible-but-unanswered offer throws {@link NeedsDecisionSignal} to round-trip a prompt. */
  interactive?: boolean;
};

/** What an offer did. `{ignored: false, steps: []}` is the inert outcome — declined, or fired and failed. */
export type StructureInterceptOutcome = {
  /**
   * True iff the structure point is **un-taken**: the caller restores the structure it just
   * decremented and ends the cascade with the hit ignored. This is the interception half — the
   * thing an event family structurally cannot do.
   */
  ignored: boolean;
  /**
   * Steps to splice into the cascade's own list — the grounded ones (the charge spend, the HP
   * restore) plus the record-only `intercept` witness that says what the offer's die did
   * (`resolution-narration` 3.3). The witness is pushed AFTER the die is drawn, never before.
   */
  steps: ResolvedStep[];
  /** Advisory narrative for the GM log. */
  warnings: string[];
};

/** The inert outcome — no interception fired. */
const NO_INTERCEPT: StructureInterceptOutcome = { ignored: false, steps: [], warnings: [] };

/**
 * The **on-a-success** effect an offer applies, held as data rather than buried in `outcome`'s body
 * so the pack-tier drift pin can compare it against the authored clause field by field. See
 * `packs/core-rulebook/entries/systems/custom-paint-job.test.ts`'s "the built-in offer and the authored clause agree" block.
 */
export type StructureInterceptSpec = {
  /** Faces on the die the offer rolls. */
  readonly sides: number;
  /** The roll succeeds at `>= threshold`. */
  readonly threshold: number;
  /** The HP the target is set to on a success (clamped into `[0, maxHp]`). */
  readonly hpOnSuccess: number;
};

/**
 * One structure-loss interception offer. A fixed, authored member (never registration-order) so the
 * recorded resolution is byte-identical across replay. `outcome` is pure over `(ctx, answer)`.
 */
export type StructureInterceptOffer = {
  /** Stable id — the registry runs fixed by its list order, and this is the prompt key's first segment. */
  readonly id: string;
  /**
   * The ability's **display name**, as a player would read it ("Custom Paint Job"). Recorded verbatim
   * onto every {@link InterceptStep} this offer emits — a genuine title, never an id run through a
   * titling transform. Authored here beside {@link spec} for the same reason the spec is: the record
   * (and therefore any prose written off it) must not depend on a compendium lookup at read time.
   */
  readonly name: string;
  /**
   * The compendium item id whose authored `action` clause grants this ability. **The only datum an
   * offer hardcodes.** Existence and charge count are read off the frozen cap that clause produced;
   * this id is just which cap to look for.
   */
  readonly carrierItemId: string;
  /** The success effect, as data, for the authored-clause drift pin. */
  readonly spec: StructureInterceptSpec;
  /** Eligible to fire on this target right now? */
  eligible(ctx: StructureInterceptContext): boolean;
  /** Which actor must answer — the prompt's `who`. Always the damaged combatant here (the ability is its own). */
  who(ctx: StructureInterceptContext): string;
  /** The stable prompt id the answer records under. */
  promptId(ctx: StructureInterceptContext): string;
  /** The human-readable prompt the decider sees. */
  prompt(ctx: StructureInterceptContext): string;
  /** The selectable options (the recorded answer is one of these ids). */
  readonly options: { id: string; label: string }[];
  /** Pure: build the outcome for a recorded answer. A non-firing answer returns {@link NO_INTERCEPT}. */
  outcome(ctx: StructureInterceptContext, answer: string): StructureInterceptOutcome;
};

/**
 * A structure-loss interception's stable recorded-answer key — keyed on the **STRUCTURE VALUE**
 * being resolved (`remainingStructure`), NOT call-order, for the identical documented reason
 * {@link import("./tables").systemTraumaPromptId} is: a big hit blowing through several structure
 * points in one `resolveDamageCascade` reaches this window once per point, each at a DIFFERENT
 * remaining-structure value, so this key is unique per decision within one resolution and stable
 * across a re-run (the iterate-pure re-run-from-top replays the SAME sequence of
 * remaining-structure values every time). A counter or a call-order index would not: an answer
 * that changes control flow would renumber every later prompt.
 *
 * Exported so a test/frontend can construct the expected key without duplicating the scheme.
 */
export function structureInterceptPromptId(offerId: string, combatantId: string, remainingStructure: number): string {
  return `structure_intercept:${offerId}:${combatantId}:${remainingStructure}`;
}

/**
 * Is `key` a recorded/broadcast answer to a structure-loss interception prompt
 * ({@link structureInterceptPromptId}), de-namespaced of any barrage `sub:<i>:` prefix the same
 * defensive way `isReactionPromptId`/`isSystemTraumaPromptId` are? Its one job is the cross-actor
 * **auto-dismiss** site (`referee/refereeIntent.ts`'s `isCrossActorAnsweredPrompt`), so a stale
 * interception modal on another client — a GM's view after the player answers — clears exactly the
 * way a Brace or System Trauma prompt's does. **Deliberately kept SEPARATE from
 * `isReactionPromptId`**, for the reason `isSystemTraumaPromptId`'s own doc gives: this is not a
 * reaction (no defender/attacker routing or attribution), so it must never be folded into
 * `encounterRoom.ts`'s reaction-routing `defenders` filter.
 */
export function isStructureInterceptPromptId(key: string): boolean {
  return key.replace(/^sub:\d+:/, "").startsWith("structure_intercept:");
}

/**
 * The frozen **Limited pool** `itemId`'s authored clause produced at spawn, with the ledger slot it
 * counts on (`pool ?? key`), or
 * `undefined` if the item authors none on this combatant. Walks `base.affordances` for a
 * **granted-class record whose `source.itemId` is `itemId`** exactly as `isSystemDestroyable`
 * does — a structural match on the record's own address, not a `<itemId>:` string-prefix test on
 * a hand-built key (`decisions/0176`; `affordance-table` Phase 3.1). The table is frozen at spawn,
 * so its order (and therefore this first-match) is replay-stable.
 *
 * The prefix test it replaced could not tell a granted composite from a talent rank; matching
 * `source.kind === "granted"` first makes that confusion unrepresentable.
 */
function frozenLimitedFor(base: Combatant["base"], itemId: string): { actionId: string; max: number } | undefined {
  for (const a of base.affordances ?? []) {
    if (a.source.kind !== "granted" || a.source.itemId !== itemId) continue;
    if (a.limited) return { actionId: a.limited.pool ?? a.key, max: a.limited.max };
  }
  return undefined;
}

/**
 * Has `itemId`'s equipped instance been destroyed (System Trauma, an earlier row of this very
 * cascade)? A destroyed system grants nothing, so its interception must go dark with it. Read off
 * the SAME `current.resources` destroyed-set every other destruction read uses, which is why the
 * caller must pass the cascade-locally-overlaid combatant.
 */
function carrierDestroyed(target: Combatant, itemId: string): boolean {
  const system = (target.base.equippedSystems ?? []).find((s) => s.id === itemId);
  if (!system) return false;
  return isItemDestroyed(target.current.resources, system.instanceId);
}

/**
 * The **charge spend**: an `expend_charges` cost settled inline into the cascade's own step list,
 * grounding to an `affordance_use_set` over `current.affordanceState`, the ONE per-affordance
 * ledger (`decisions/0176`). It reads the pool's count off the cascade-local overlaid `target`
 * (`ExpendChargesEvent.read`), and keeps the presence-carrying `prev`: omitted for a
 * genuinely-absent entry so `invertStep` restores TRUE absence on undo.
 *
 * **`affordance-table` Phase 3.1 gave this its ledger write.** Through Phases 1-2 it emitted only
 * the `scene_use:` `resource_set` and had no `affordanceState` twin — harmless while the prefix
 * still existed and `canUseSceneAction` still read it, and a silent no-write the moment the prefix
 * went. It writes the ledger and nothing else now, matching every other spend site.
 *
 * **Emitting the spend as a step in the cascade's own list is deliberate.** There is no step-level
 * "spend one granted-action use"; the spend otherwise exists only as the `spend_action_use` sibling
 * command `refereeIntent` folds *after* an early return for zero-step activations
 * (`referee/refereeIntent.ts`), which is why a failed press of a pressed action costs nothing today.
 * A step in this list never reaches that early return, so the charge burns on a FAILED roll — which
 * is RAW ("this system can only be used once before each Full Repair"; the use is spent on the roll,
 * not on the success). The general defect is filed separately as
 * `projects/failed-activation-costs-nothing.md`; this path simply never enters it.
 */
function sceneUseConsumeSteps(target: Combatant, actionId: string): ResolvedStep[] {
  return spendStepsNow([{ kind: "expend_charges", carrier: target.id, pool: actionId, amount: 1, read: { uses: target.current.affordanceState?.[actionId]?.uses } }], undefined, undefined);
}

/**
 * The **record-only witness** an offer leaves on the resolution's own step list
 * (`resolution-narration` 3.3). Built here rather than inline at its two call sites so a fired
 * record and a declined one cannot drift in shape.
 *
 * It records nothing the offer's data does not already hold — the ability's display name and
 * carrier id, the die and the threshold, plus the face rolled and the verdict — so a reader can say
 * *why* a structure point stood or did not without re-deriving anything. The existing advisory
 * `warnings` prose is untouched and still emitted alongside it.
 *
 * `roll` is omitted, not zeroed, on a decline: no die was drawn, and a `0` face would be a lie the
 * record cannot distinguish from a real one.
 */
function interceptStep(
  offer: StructureInterceptOffer,
  ctx: StructureInterceptContext,
  verdict: { roll: number; ignored: boolean } | { declined: true },
): InterceptStep {
  return {
    op: "intercept",
    target: ctx.target.id,
    source: { name: offer.name, id: offer.carrierItemId },
    sides: offer.spec.sides,
    threshold: offer.spec.threshold,
    ..."declined" in verdict ? { ignored: false, declined: true as const } : { roll: verdict.roll, ignored: verdict.ignored },
  };
}

/** `ms_custom_paint_job` — the one fielded carrier (Fire Horse). */
const CUSTOM_PAINT_JOB_ITEM = "ms_custom_paint_job";

/**
 * **Custom Paint Job** (GMS system, LL0). RAW: *"When you take structure damage, roll 1d6. On a 6,
 * you return to 1 HP and ignore the damage — the hit simply 'scratched your paint'. This system can
 * only be used once before each Full Repair."*
 *
 * The choice is RAW's own — the charge is once per Full Repair, so "save it for a worse hit" is a
 * real decision, and it is the affordance the player has at this window. `use` spends the charge and
 * rolls; `save` declines and costs nothing.
 *
 * Eligibility hardcodes nothing but {@link carrierItemId}: the frozen `limited: {max: 1}` pool the
 * spawn's overlay-mint arm froze from the authored clause's `limitedUses` facet is what says the
 * ability exists, and `canUseSceneAction` against
 * `current.affordanceState` is what says it still has a charge.
 */
const CUSTOM_PAINT_JOB_OFFER: StructureInterceptOffer = {
  id: "custom_paint_job",
  name: "Custom Paint Job",
  carrierItemId: CUSTOM_PAINT_JOB_ITEM,
  spec: { sides: 6, threshold: 6, hpOnSuccess: 1 },
  eligible: (ctx) => {
    const frozen = frozenLimitedFor(ctx.target.base, CUSTOM_PAINT_JOB_ITEM);
    if (!frozen) return false; // no authored clause on this carrier ⇒ no cap ⇒ no offer.
    if (carrierDestroyed(ctx.target, CUSTOM_PAINT_JOB_ITEM)) return false;
    return canUseSceneAction(ctx.target.current.affordanceState, frozen.actionId, frozen.max);
  },
  who: (ctx) => ctx.target.id,
  promptId: (ctx) => structureInterceptPromptId("custom_paint_job", ctx.target.id, ctx.remainingStructure),
  prompt: (ctx) =>
    `Custom Paint Job? ${ctx.target.name} is taking structure damage — spend the charge to roll 1d6, ` +
    `and on a 6 return to 1 HP and ignore the damage. Once before each Full Repair.`,
  options: [
    { id: INTERCEPT_USE_OPTION, label: "Scratch the paint (spend the charge, roll 1d6)" },
    { id: INTERCEPT_SAVE_OPTION, label: "Save it" },
  ],
  outcome: (ctx, answer) => {
    if (answer !== INTERCEPT_USE_OPTION) return NO_INTERCEPT;
    const frozen = frozenLimitedFor(ctx.target.base, CUSTOM_PAINT_JOB_ITEM);
    if (!frozen) return NO_INTERCEPT; // unreachable past `eligible`; belt-and-braces for a direct call.

    // The charge is spent on the ROLL, not on the success (RAW) — emitted before the branch so a
    // failed roll still burns it.
    const steps: ResolvedStep[] = sceneUseConsumeSteps(ctx.target, frozen.actionId);

    // ─────────────────────────────────────────────────────────────────────────────────────────
    // THE DIE IS DRAWN HERE — AFTER the answer is known — AND IT MUST STAY HERE.
    //
    // `recordingDiceSource` (`packages/rules/src/dice.ts`) is STRICTLY POSITIONAL: a bare draw
    // counter, no key, no `sides` check, and no guard of any kind. The invariant that makes it
    // correct (same file) is that resolution is pure over `(state, intent, recordedInputLog)`, so a
    // recorded answer may only influence control flow **at and after its own halt** — never the
    // dice drawn before it.
    //
    // Roll BEFORE raising the prompt and that invariant breaks: run 1 halts on the prompt having
    // already drawn this d6, and the answered re-run takes the `save` branch, which draws nothing.
    // Every draw after this point then shifts one slot earlier in the recorded log, so the
    // Structure Damage pool and the System Trauma category d6 replay from the WRONG positions.
    // Nothing errors. Nothing warns. The table just sees a different consequence row than the one
    // the authority rolled — and it would read at the table as "it procced twice".
    //
    // Rule: an interception offer draws dice only inside `outcome`, which runs only once an answer
    // is in hand. Never in `eligible`, never in `prompt`, never before the throw.
    // ─────────────────────────────────────────────────────────────────────────────────────────
    const roll = ctx.dice.roll(CUSTOM_PAINT_JOB_OFFER.spec.sides);
    if (roll < CUSTOM_PAINT_JOB_OFFER.spec.threshold) {
      // The record-only witness, pushed AFTER the draw and never before it — building the step
      // cannot be what moves the die (see the block above). The charge spend precedes it in the
      // list because that is the order the two happened in.
      steps.push(interceptStep(CUSTOM_PAINT_JOB_OFFER, ctx, { roll, ignored: false }));
      return {
        ignored: false,
        steps,
        warnings: [
          `Custom Paint Job: ${ctx.target.name} spent its charge and rolled ${roll} — no good. ` +
            `The structure damage stands.`,
        ],
      };
    }

    // "You return to 1 HP" — the same clamp+delta shape the `set_hp` atom compiles to, computed
    // against the cascade's LOCAL hp (0 in an HP-driven cascade), not `target.current.hp` (the
    // cascade never mutates `target`, so the committed value is still the pre-hit HP).
    //
    // **Floored at 0 — a RESTORE, never a reduction.** RAW's "you return to 1 HP" describes being
    // pulled back from the 0 HP that forced the structure loss, and in an HP-driven cascade
    // `simulatedHp` is always 0, so the floor is inert there (the delta is `+1`). A SUBSTITUTED
    // structure point (`tables.ts`'s `substituteStructurePoint` — Sniper's Mark deals structure
    // damage with the HP track untouched) reaches this window at the target's ordinary HP, where an
    // unfloored `after - simulatedHp` would emit a large NEGATIVE delta and turn a successful
    // interception into the worst hit of the fight.
    steps.push(interceptStep(CUSTOM_PAINT_JOB_OFFER, ctx, { roll, ignored: true }));
    const after = clampHp(ctx.target, CUSTOM_PAINT_JOB_OFFER.spec.hpOnSuccess);
    const delta = Math.max(0, after - ctx.simulatedHp);
    if (delta !== 0) steps.push({ op: "hp_delta", target: ctx.target.id, delta });
    return {
      ignored: true,
      steps,
      warnings: [
        `Custom Paint Job: ${ctx.target.name} rolled a ${roll} — the hit simply scratched the paint. ` +
          `Back to ${ctx.simulatedHp + delta} HP, and the structure damage is ignored.`,
      ],
    };
  },
};

/**
 * The registry — a **fixed, authored list**, never registration-order, so a recorded resolution is
 * byte-identical across replay. Exported for the pack-tier drift pin.
 */
export const STRUCTURE_INTERCEPT_OFFERS: readonly StructureInterceptOffer[] = [CUSTOM_PAINT_JOB_OFFER];

/** What {@link gatherStructureIntercept} settles, plus the ids the caller must record as offered. */
export type StructureInterceptResult = StructureInterceptOutcome & {
  /** Offer ids resolved in this call — the caller folds these into its cascade-local accumulator. */
  offered: string[];
};

/** Nothing eligible / nothing fired. */
const NO_INTERCEPT_RESULT: StructureInterceptResult = { ignored: false, steps: [], warnings: [], offered: [] };

/**
 * Consult the registry at the structure-loss interception window. Runs every ELIGIBLE offer in the
 * fixed list order, in the same recorded / interactive-throw / non-interactive-default-decline
 * shape `gatherReaction` uses, and **stops at the first offer that ignores the damage** (there is
 * nothing left to intercept once the structure point is un-taken).
 *
 * **Throws {@link NeedsDecisionSignal}** (carrying `who` = the offer's decider) when an eligible
 * offer is interactive and unanswered — `resolveDamageCascade` lets it propagate exactly as the
 * System Trauma pick does, the resolver catches it and returns `needs_decision`, and the answered
 * re-run replays this whole function from the top. Offers already resolved earlier in the SAME call
 * are simply recomputed identically (pure over `(ctx, choices)`).
 *
 * **Every raised offer leaves a record.** A firing offer records its own `intercept` step inside
 * `outcome` (after its die is drawn); an explicitly DECLINED one gets one here. The non-interactive
 * default-decline path records nothing — see the `declined` flag's own comment in the body.
 *
 * `alreadyOffered` is the cascade-local accumulator: a hit that blows through several structure
 * points reaches this window once per point, and the charge spend is not folded into
 * `current.resources` until the whole batch commits, so without it the same one-charge offer would
 * be raised at every point. It mirrors `resolveDamageCascade`'s own `destroyedThisCascade`, and
 * exists for the same reason.
 */
export function gatherStructureIntercept(
  ctx: StructureInterceptContext,
  alreadyOffered: Record<string, number> | undefined,
): StructureInterceptResult {
  let result: StructureInterceptResult | undefined;
  const offered: string[] = [];
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  for (const offer of STRUCTURE_INTERCEPT_OFFERS) {
    if (alreadyOffered?.[offer.id] !== undefined) continue;
    if (!offer.eligible(ctx)) continue;
    const promptId = offer.promptId(ctx);
    const optionIds = offer.options.map((o) => o.id);
    const recorded = recordedAnswer(ctx.choices, promptId, optionIds);
    let outcome: StructureInterceptOutcome;
    /**
     * A recorded answer that did NOT fire the offer. It earns a record-only {@link InterceptStep}
     * (`declined: true`, no roll) for the developer's standing rule that a player never wonders
     * whether an ability fired: the offer was raised, somebody answered "save", and without this
     * the whole exchange leaves no trace on the record at all.
     *
     * **Deliberately NOT set on the non-interactive default-decline path below.** Nothing was
     * offered to anybody there — no prompt was raised — so "offered, declined" would be a lie, and
     * recording it would break the property that doc comment names: a non-interactive cascade stays
     * byte-identical to one with no interception window at all.
     */
    let declined = false;
    if (recorded !== undefined && optionIds.includes(recorded)) {
      outcome = offer.outcome(ctx, recorded);
      declined = recorded !== INTERCEPT_USE_OPTION;
    } else if (ctx.interactive) {
      throw new NeedsDecisionSignal(promptId, offer.prompt(ctx), { options: offer.options, who: offer.who(ctx) });
    } else {
      // Non-interactive (replay / CLI / whichever cascade callers pass no `choices` today): the
      // deterministic default — decline. This is what keeps every non-interactive call site's
      // behaviour byte-identical to before this window existed. The count was "seven of the
      // eight" when this was written and `silent-equipment-autopick` has been shrinking it since;
      // read the flag, never a caller census.
      outcome = offer.outcome(ctx, INTERCEPT_SAVE_OPTION);
    }
    offered.push(offer.id);
    steps.push(...outcome.steps);
    if (declined) steps.push(interceptStep(offer, ctx, { declined: true }));
    warnings.push(...outcome.warnings);
    if (outcome.ignored) {
      result = { ignored: true, steps, warnings, offered };
      break;
    }
  }
  return result ?? (offered.length === 0 ? NO_INTERCEPT_RESULT : { ignored: false, steps, warnings, offered });
}
